import React, { useState, useEffect } from 'react';
import { useFlow } from './hooks/useFlow';
import { useNavigate } from 'react-router-dom';

const SubscriptionPlansPage = () => {
  const { loading, error, createSubscription, getAvailablePlans } = useFlow();
  const [plans, setPlans] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [isAnnual, setIsAnnual] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    loadPlans();
  }, []);

  const loadPlans = async () => {
    try {
      const availablePlans = [
        {
          id: 'plan-basico',
          name: 'Plan Básico',
          price: 0,
          currency: 'CLP',
          interval: 'mensual',
          features: [
            'Acceso a contenido básico',
            'Anuncios incluidos',
          ],
          popular: false
        },
        {
          id: 'plan-premium',
          name: 'Plan Premium',
          price: 4990,
          currency: 'CLP',
          interval: 'mensual',
          features: [
            'Acceso a todo el contenido',
            'Sin anuncios',
            'Contenido exclusivo',
            'Soporte prioritario',
          ],
          popular: true
        },
        {
          id: 'plan-anual',
          name: 'Plan Premium',
          price: 49990,
          currency: 'CLP',
          interval: 'anual',
          features: [
            'Acceso a todo el contenido',
            'Sin anuncios',
            'Contenido exclusivo',
            'Soporte prioritario',
            '2 meses gratis',            
          ],
          popular: false
        }
      ];
      setPlans(availablePlans);
    } catch (err) {
      console.error('Error cargando planes:', err);
    }
  };

  const handleSelectPlan = async (plan) => {
    setSelectedPlan(plan);
    
    try {
      const subscription = await createSubscription({
        planId: plan.id,
        planName: plan.name,
        amount: plan.price,
        currency: plan.currency,
        interval: plan.interval
      });
      
      navigate('/suscripcion-exitosa', { 
        state: { 
          subscription,
          plan 
        }
      });
    } catch (err) {
      console.error('Error al crear suscripción:', err);
      alert('Error al procesar la suscripción. Por favor intenta nuevamente.');
    }
  };

  const formatPrice = (price) => {
    if (price === 0) return 'Gratis';
    return new Intl.NumberFormat('es-CL', {
      style: 'currency',
      currency: 'CLP',
      minimumFractionDigits: 0
    }).format(price);
  };

  const getDisplayPlans = () => {
    const basicPlan = plans.find(p => p.id === 'plan-basico');
    const premiumPlan = isAnnual 
      ? plans.find(p => p.id === 'plan-anual')
      : plans.find(p => p.id === 'plan-premium');
    
    return [basicPlan, premiumPlan].filter(Boolean);
  };

  const displayPlans = getDisplayPlans();

  return (
    <div className="min-h-screen bg-black text-white py-20 relative overflow-hidden">
      {/* Animated background elements */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-20 left-10 w-32 h-32 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full animate-pulse"></div>
        <div className="absolute top-40 right-20 w-24 h-24 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-full animate-bounce delay-1000"></div>
        <div className="absolute bottom-40 left-1/4 w-40 h-40 bg-gradient-to-br from-green-400 to-blue-500 rounded-full animate-pulse delay-2000"></div>
        <div className="absolute bottom-20 right-1/3 w-28 h-28 bg-gradient-to-br from-red-400 to-purple-500 rounded-full animate-bounce delay-500"></div>
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="text-center mb-6">
          <h1 className="text-4xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-purple-400 via-pink-500 to-yellow-400 bg-clip-text text-transparent animate-pulse pb-3">
            Elige tu Plan
          </h1>
          <p className="text-xl text-gray-300 max-w-2xl mx-auto">
            Desbloquea todo el contenido
            y disfruta de shows exclusivos
          </p>
        </div>

        {/* Animated Toggle Switch */}
        <div className="flex justify-center mb-8"> {/* Cambié de mb-12 a mb-8 */}
          <div className="bg-gray-800 p-1 rounded-full flex items-center shadow-2xl border border-purple-500/30 hover:border-purple-500/50 transition-all duration-300">
            <button
              onClick={() => setIsAnnual(false)}
              className={`px-6 py-2 rounded-full transition-all duration-500 transform ${
                !isAnnual 
                  ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white font-bold scale-105 shadow-lg shadow-purple-500/25' 
                  : 'text-gray-300 hover:text-white hover:scale-105'
              }`}
            >
              Mensual
            </button>
            <button
              onClick={() => setIsAnnual(true)}
              className={`px-6 py-2 rounded-full transition-all duration-500 flex items-center transform ${
                isAnnual 
                  ? 'bg-gradient-to-r from-yellow-400 to-orange-500 text-black font-bold scale-105 shadow-lg shadow-yellow-500/25' 
                  : 'text-gray-300 hover:text-white hover:scale-105'
              }`}
            >
              Anual
              <span className="ml-2 bg-gradient-to-r from-green-400 to-green-500 text-black text-xs px-2 py-1 rounded-full animate-pulse">
                -17%
              </span>
            </button>
          </div>
        </div>

        {error && (
          <div className="bg-gradient-to-r from-red-500 to-red-600 text-white p-4 rounded-lg mb-8 max-w-md mx-auto shadow-lg animate-shake">
            {error}
          </div>
        )}

        <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
          {displayPlans.map((plan, index) => (
            <div
              key={plan.id}
              className={`
                relative bg-gray-900 rounded-xl p-8 border-2 transition-all duration-500 transform hover:scale-105
                ${plan.id !== 'plan-basico'
                  ? 'border-purple-500 shadow-xl shadow-purple-500/25 animate-pulse-border' 
                  : 'border-gray-700 hover:border-purple-400 hover:shadow-lg hover:shadow-purple-400/20'
                }
              `}
              style={{
                animationDelay: `${index * 200}ms`
              }}
            >
             {plan.id !== 'plan-basico' && (
                <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                  <span className="bg-gradient-to-r from-yellow-400 to-orange-500 text-black px-4 py-1 rounded-full text-sm font-bold animate-bounce shadow-lg">
                    {plan.interval === 'anual' ? 'MEJOR OFERTA' : 'TODO INCLUIDO'}
                  </span>
                </div>
              )}

              <div className="text-center mb-8">
                <h3 className={`text-2xl font-bold mb-4 ${
                  plan.id !== 'plan-basico' 
                    ? 'bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent' 
                    : 'text-white'
                }`}>
                  {plan.name}
                </h3>
                <div className="mb-2">
                  <span className={`text-4xl font-bold ${
                    plan.price === 0 
                      ? 'bg-gradient-to-r from-green-400 to-green-500 bg-clip-text text-transparent' 
                      : plan.id !== 'plan-basico'
                        ? 'bg-gradient-to-r from-yellow-400 to-orange-500 bg-clip-text text-transparent'
                        : 'text-white'
                  }`}>
                    {formatPrice(plan.price)}
                  </span>
                  {plan.price !== 0 && (
                    <span className="text-gray-400">/{plan.interval}</span>
                  )}
                </div>
                {plan.interval === 'anual' && (
                  <div className="space-y-1">
                    <p className="text-green-400 text-sm font-semibold animate-pulse">¡Ahorra 2 meses!</p>
                    <p className="text-gray-400 text-xs">
                      Equivale a {formatPrice(Math.round(plan.price / 12))}/mes
                    </p>
                  </div>
                )}
              </div>

              <ul className="space-y-4 mb-8">
                {plan.features.map((feature, featureIndex) => (
                  <li 
                    key={featureIndex} 
                    className="flex items-center opacity-0 animate-fade-in-up"
                    style={{
                      animationDelay: `${(index * 200) + (featureIndex * 100)}ms`
                    }}
                  >
                    <svg className="w-5 h-5 text-green-400 mr-3 animate-pulse" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                    </svg>
                    {feature}
                  </li>
                ))}
              </ul>

              {/* Solo mostrar botón si NO es plan básico */}
              {plan.id !== 'plan-basico' && (
                <button
                  onClick={() => handleSelectPlan(plan)}
                  disabled={loading && selectedPlan?.id === plan.id}
                  className="w-full py-3 px-6 rounded-lg font-bold transition-all duration-500 transform hover:scale-105 shadow-lg bg-gradient-to-r from-purple-500 to-pink-500 text-white hover:from-purple-600 hover:to-pink-600 shadow-purple-500/25 hover:shadow-purple-500/40 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                >
                  {loading && selectedPlan?.id === plan.id ? (
                    <span className="flex items-center justify-center">
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Procesando...
                    </span>
                  ) : (
                    'Seleccionar Plan'
                  )}
                </button>
              )}

              {/* Mensaje para plan básico */}
              {plan.id === 'plan-basico' && (
                <div className="text-center">
                  <p className="text-gray-400 text-sm">
                    ¡Ya tienes acceso a este plan!
                  </p>
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="text-center mt-12">
          <p className="text-gray-400 mb-4">
            ¿Tienes dudas? Revisa nuestras{' '}
            <button 
              onClick={() => navigate('/preguntas-frecuentes')}
              className="text-transparent bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text hover:from-purple-300 hover:to-pink-300 transition-all duration-300 font-semibold"
            >
              preguntas frecuentes
            </button>
          </p>
          <p className="text-sm text-gray-500">
            Puedes cancelar tu suscripción en cualquier momento desde tu perfil
          </p>
        </div>
      </div>

      <style jsx>{`
        @keyframes fade-in-up {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes pulse-border {
          0%, 100% {
            border-color: rgb(168 85 247 / 0.5);
          }
          50% {
            border-color: rgb(168 85 247);
          }
        }
        
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          25% { transform: translateX(-5px); }
          75% { transform: translateX(5px); }
        }
        
        .animate-fade-in-up {
          animation: fade-in-up 0.6s ease-out forwards;
        }
        
        .animate-pulse-border {
          animation: pulse-border 2s ease-in-out infinite;
        }
        
        .animate-shake {
          animation: shake 0.5s ease-in-out;
        }
      `}</style>
    </div>
  );
};

export default SubscriptionPlansPage;